import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Alert, Badge, Button, Card, FormField } from "../../../components/ui";
import { ApiError } from "../../../lib/api/client";
import {
  buildVoiceRag,
  searchVoiceRag,
  type VoiceOpenAIKeyStatus,
  type VoiceRagSearchResult,
  type VoiceRagStatus,
} from "../api/voiceApi";

const statusLabel: Record<string, string> = {
  empty: "尚未建立",
  building: "建置中",
  ready: "可查詢",
  failed: "建置失敗",
};

const statusVariant: Record<string, string> = {
  empty: "secondary",
  building: "warning",
  ready: "success",
  failed: "danger",
};

export function RagCard({
  status,
  keyStatus,
}: {
  status: VoiceRagStatus | undefined;
  keyStatus: VoiceOpenAIKeyStatus | undefined;
}) {
  const queryClient = useQueryClient();
  const isBuilding = status?.state === "building";
  const keyReady = keyStatus?.validation === "valid";
  const [file, setFile] = useState<File>();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<VoiceRagSearchResult[]>([]);
  const [notice, setNotice] = useState<string>();
  const [buildError, setBuildError] = useState<string>();
  const [searchError, setSearchError] = useState<string>();
  const [building, setBuilding] = useState(false);
  const [searching, setSearching] = useState(false);

  const submitBuild = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (!file) {
      setBuildError("請選擇 ZIP 檔案。");
      return;
    }
    if (!keyReady) {
      setBuildError("請先完成 OpenAI API Key 驗證，才能上傳並建置 RAG 教材。");
      return;
    }
    setBuilding(true);
    setNotice(undefined);
    setBuildError(undefined);
    try {
      const job = await buildVoiceRag(file);
      setResults([]);
      queryClient.setQueryData(["voice", "rag-status"], (current: VoiceRagStatus | undefined) => ({
        state: job.state,
        jobId: job.jobId,
        documentCount: current?.documentCount ?? 0,
        chunkCount: current?.chunkCount ?? 0,
        ignoredFileCount: current?.ignoredFileCount ?? 0,
        lastSuccessAt: current?.lastSuccessAt ?? null,
        error: null,
      }));
      setNotice("RAG 建置已開始，系統會自動更新目前狀態。");
      setFile(undefined);
      const fileInput = form.elements.namedItem("voice-rag-file") as HTMLInputElement | null;
      if (fileInput) fileInput.value = "";
      await queryClient.invalidateQueries({ queryKey: ["voice", "rag-status"] });
    } catch (caught) {
      setBuildError(caught instanceof ApiError ? caught.message : "RAG 建置失敗，請稍後再試。");
    } finally {
      setBuilding(false);
    }
  };

  const submitSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isBuilding) {
      setResults([]);
      setSearchError("教材建置中，請等待狀態變成可查詢後再搜尋。");
      return;
    }
    if (!query.trim()) {
      setSearchError("請輸入搜尋問題。");
      return;
    }
    setSearching(true);
    setSearchError(undefined);
    try {
      const next = await searchVoiceRag(query);
      setResults(next);
      if (next.length === 0) setSearchError("找不到相關內容，請換一個關鍵字試試。");
    } catch (caught) {
      setResults([]);
      setSearchError(caught instanceof ApiError ? caught.message : "RAG 搜尋失敗，請稍後再試。");
    } finally {
      setSearching(false);
    }
  };

  return (
    <Card title="RAG 教材建置">
      <p className="text-muted mb-3">
        上傳包含 <code>.txt</code> 或 <code>.md</code> 文字內容的 ZIP 檔，系統會建立個人知識庫供後續對話查詢。
      </p>
      {notice && <Alert variant="success">{notice}</Alert>}
      {buildError && <Alert variant="danger">{buildError}</Alert>}
      <div className="mb-3">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span className="text-muted">目前狀態</span>{" "}
          <Badge variant={statusVariant[status?.state ?? "empty"] ?? "secondary"}>
            {statusLabel[status?.state ?? "empty"] ?? "尚未建立"}
          </Badge>
        </div>
        <dl className="mb-0">
          <dt className="small text-muted mb-1">文件數量</dt>
          <dd>{status?.documentCount ?? 0}</dd>
          <dt className="small text-muted mb-1">切片數量</dt>
          <dd>{status?.chunkCount ?? 0}</dd>
          <dt className="small text-muted mb-1">忽略檔案數</dt>
          <dd>{status?.ignoredFileCount ?? 0}</dd>
          {status?.error && (
            <>
              <dt className="small text-muted mb-1">最近錯誤</dt>
              <dd>{status.error}</dd>
            </>
          )}
        </dl>
      </div>
      <form className="mb-4" onSubmit={(event) => void submitBuild(event)}>
        <label className="form-label font-weight-bold" htmlFor="voice-rag-file">
          教材 ZIP 檔案
        </label>
        <input
          id="voice-rag-file"
          name="voice-rag-file"
          className="form-control mb-3"
          type="file"
          accept=".zip,application/zip"
          disabled={!keyReady || building}
          onChange={(event) => setFile(event.target.files?.[0])}
        />
        <Button type="submit" loading={building} disabled={!keyReady}>
          上傳並建置
        </Button>
      </form>
      {!keyReady && (
        <Alert variant="warning">請先在上方完成 OpenAI API Key 驗證，通過後才能上傳並建置 RAG 教材。</Alert>
      )}
      <form onSubmit={(event) => void submitSearch(event)}>
        <FormField
          label="搜尋測試"
          value={query}
          placeholder="例如：品牌理念、服務、營業時間"
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button type="submit" variant="secondary" loading={searching} disabled={isBuilding}>
          搜尋知識庫
        </Button>
      </form>
      {isBuilding && (
        <Alert variant="info">新教材尚在建置中，搜尋結果會在建置完成後才更新。</Alert>
      )}
      {searchError && <Alert variant="warning">{searchError}</Alert>}
      {results.length > 0 && (
        <div className="mt-4">
          {results.map((result, index) => (
            <Card key={`${result.source ?? "unknown"}-${result.sectionIndex ?? 0}-${index}`} title={result.source ?? "未命名來源"}>
              <div className="small text-muted mb-2">
                相似度 {result.score.toFixed(4)}
                {typeof result.sectionIndex === "number" ? ` · 段落 ${result.sectionIndex + 1}` : ""}
              </div>
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{result.content}</p>
            </Card>
          ))}
        </div>
      )}
    </Card>
  );
}
